// Auto-detection: probe an unknown target and determine its protocol + board.
//
// Tries each protocol in order with a short timeout:
//   1. STK500v1 — GET_SYNC (most common: Uno, Nano, Pro Mini)
//   2. STK500v2 — SIGN_ON  (Mega 2560, Mega 1280)
//   3. AVR109   — get software ID (Leonardo, Micro, Pro Micro)
//
// UPDI devices require a BREAK condition first and cannot be auto-detected
// in this manner — callers must select UPDI explicitly.
//
// Usage:
//   const result = await autoDetect(transport, { probeTimeoutMs: 300 });
//   if (result) {
//     console.log(result.protocol, result.board?.name, result.signature);
//   }
import { STK500 } from './stk500.js';
import { STK500v2 } from './protocol/stk500v2/programmer.js';
import { AVR109 } from './protocol/avr109/programmer.js';
import { BOARDS, detectBoardBySignature, } from './boards/database.js';
/** Probe board for a given transport — detect protocol and signature. */
export async function autoDetect(transport, opts = {}) {
    const timeout = opts.probeTimeoutMs ?? 300;
    const quiet = opts.quiet !== false; // default quiet=true
    // Minimal probe board — only timeout matters for probing
    const probeBoard = {
        ...BOARDS['arduino-uno'],
        timeout,
    };
    // ── 1. STK500v1 ────────────────────────────────────────────────────────────
    try {
        const stk = new STK500(transport, probeBoard, {
            quiet,
            retry: { syncAttempts: 1, retryDelayMs: 0 },
        });
        await stk.sync(1);
        const sig = await stk.getSignature();
        const board = detectBoardBySignature(sig) ?? undefined;
        return { protocol: 'stk500v1', signature: sig, board };
    }
    catch { /* not STK500v1 */ }
    // ── 2. STK500v2 ────────────────────────────────────────────────────────────
    try {
        const probeV2Board = { ...BOARDS['arduino-mega2560'], timeout };
        const stk2 = new STK500v2(transport, probeV2Board, {
            quiet,
            retry: { syncAttempts: 1, retryDelayMs: 0 },
        });
        await stk2.sync(1);
        const sig = await stk2.readSignature();
        const board = detectBoardBySignature(sig) ?? undefined;
        return { protocol: 'stk500v2', signature: sig, board };
    }
    catch { /* not STK500v2 */ }
    // ── 3. AVR109 ──────────────────────────────────────────────────────────────
    try {
        const probeAvrBoard = { ...BOARDS['arduino-leonardo'], timeout };
        const avr = new AVR109(transport, probeAvrBoard, { quiet });
        await avr.sync(1);
        const sig = await avr.getSignature();
        const board = detectBoardBySignature(sig) ?? undefined;
        return { protocol: 'avr109', signature: sig, board };
    }
    catch { /* not AVR109 */ }
    return null;
}
//# sourceMappingURL=autoDetect.js.map